const ZH_LOCALE = 'zh-CN';
const CJK_RE = /[㐀-鿿]/;

type Translate = (key: string, params?: Record<string, string | number>) => string;

export function formatNumber(value: number): string {
  return value.toLocaleString(ZH_LOCALE);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(ZH_LOCALE);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(ZH_LOCALE, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString(ZH_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function mappedLabel(t: Translate, prefix: string, value: string | null | undefined): string {
  if (!value) return t('common.none');
  const key = `${prefix}.${value}`;
  const label = t(key);
  return label === key ? value : label;
}

export function planLabel(t: Translate, plan: string | null | undefined): string {
  return mappedLabel(t, 'plan', plan);
}

export function roleLabel(t: Translate, role: string | null | undefined): string {
  return mappedLabel(t, 'role', role);
}

function hasChinese(value: string | null | undefined): boolean {
  return Boolean(value && CJK_RE.test(value));
}

// ─── 표시값 ──────────────────────────────────────────────────────────
// 관리 콘솔은 인증된 owner/admin 전용 운영 도구다. 회원가입 심사·사용자
// 관리를 하려면 신청자가 실제로 입력한 값이 그대로 보여야 한다.
// 따라서 마스킹/해시 없이 원본을 노출한다. (영문 테스트 워크스페이스명만
// 가독성을 위해 알려진 중국어 라벨로 치환한다.)

export function displayWorkspaceName(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (hasChinese(trimmed)) return trimmed;

  const known: Array<[RegExp, string]> = [
    [/^Double Approve WS$/i, '重复审批工作区'],
    [/^Resend Workspace$/i, '重发验证码工作区'],
    [/^Issued Workspace$/i, '已开通工作区'],
    [/^Reopen Workspace$/i, '重新审核工作区'],
    [/^Test Workspace$/i, '测试工作区'],
    [/^Min Workspace$/i, '最小配置工作区'],
    [/^Grant Test Workspace$/i, '预登记测试工作区'],
    [/^OptD-WS-/i, '选项 D 工作区'],
  ];
  for (const [pattern, label] of known) {
    if (pattern.test(trimmed)) return label;
  }
  return trimmed;
}

export function displayPersonName(value: string | null | undefined, _fallbackSeed?: string | null): string | null {
  const source = value?.trim();
  return source || null;
}

export function displayEmail(value: string | null | undefined, _fallbackSeed?: string | null): string | null {
  const source = value?.trim();
  return source || null;
}

export function displayIdentifier(value: string | null | undefined, _label = '编号'): string | null {
  if (!value) return null;
  return value;
}

export function displayDeviceName(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed || null;
}

export function displayAuditAction(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed || null;
}

export function displayReferralCode(value: string | null | undefined): string | null {
  if (!value) return null;
  return value;
}

export function displayTelegramHandle(value: string | null | undefined): string | null {
  if (!value) return null;
  return value;
}
